import { useCallback, useMemo, useState } from 'react'

import { useFormContext, y } from '@circlefin/form'
import { createFormInput } from '@circlefin/form/Form.Input'
import { L3OnboardingRegulators } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { L3OnboardingControl } from '../../../../containers/L3OnboardingControl/L3OnboardingControl'
import { requiredMessage } from '../../../yup/utils'
import { L3OnboardingMultiSelectCombobox } from '../../L3OnboardingMultiSelectCombobox'

import type { L3OnboardingFieldNameType } from '../../../../containers/L3OnboardingControl/types'
import type { L3OnboardingMultiSelectComboboxVariantProps } from '../types'
import type { MultiSelectComboboxItem } from '@circlefin/components/lib/MultiSelectCombobox'

export interface RegulatorsProps
  extends L3OnboardingMultiSelectComboboxVariantProps {
  /**
   * Description field shown if "Other" option selected.
   */
  descriptionName: L3OnboardingFieldNameType
}

export const regulatorsSchema = y
  .array()
  .of(y.mixed<L3OnboardingRegulators>())
  .min(1, requiredMessage)

const Input = createFormInput()

export const Regulators: React.FC<RegulatorsProps> = ({
  descriptionName,
  name,
  ...props
}) => {
  const { t } = useTranslation('l3Onboarding')
  const { watch } = useFormContext()
  const [showTextArea, setShowTextArea] = useState<boolean>(false)

  const regulatorsValue = watch(name) as
    | Array<L3OnboardingRegulators | undefined>
    | undefined

  const items: Array<MultiSelectComboboxItem<L3OnboardingRegulators>> =
    useMemo(() => {
      const values = Object.values(L3OnboardingRegulators)
        .filter((value) => value !== L3OnboardingRegulators.other)
        .map((option) => {
          return {
            label: t(
              `forms.licensingAndRegistration.financialRegulatoryAuthorityName.options.${L3OnboardingRegulators[option]}`,
            ),
            value: L3OnboardingRegulators[option],
          }
        })

      return [
        ...values,
        {
          label: t(
            `forms.licensingAndRegistration.financialRegulatoryAuthorityName.options.${L3OnboardingRegulators.other}`,
          ),
          value: L3OnboardingRegulators.other,
        },
      ]
    }, [t])

  const handleOtherSelection = useCallback(
    (change: Array<L3OnboardingRegulators | undefined>) => {
      if (change.includes(L3OnboardingRegulators.other)) {
        setShowTextArea(true)
      } else if (showTextArea) {
        setShowTextArea(false)
      }
    },
    [showTextArea],
  )

  const showOtherInput = useMemo(
    () =>
      regulatorsValue?.includes(L3OnboardingRegulators.other) || showTextArea,
    [regulatorsValue, showTextArea],
  )

  return (
    <div className="space-y-4">
      <L3OnboardingControl name={name}>
        <L3OnboardingMultiSelectCombobox
          items={items}
          label={t`forms.licensingAndRegistration.financialRegulatoryAuthorityName.label`}
          name={name}
          onChange={handleOtherSelection}
          {...props}
        />
      </L3OnboardingControl>
      {showOtherInput && (
        <L3OnboardingControl name={descriptionName}>
          <Input
            className="w-full"
            label={t`forms.licensingAndRegistration.financialRegulatoryAuthorityName.otherLabel`}
            name={descriptionName}
            {...props}
          />
        </L3OnboardingControl>
      )}
    </div>
  )
}
